import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  deleteUserProfileData,
  getUserProfile,
  getCoachByUid,
  getCoachById,
  removeApprovedCoach,
  removeCoachFromFirestoreList,
  deleteAllAvailabilitySlotsForCoach,
  sendCoachRemovalEmails
} from '../utils/firebaseUtils';
import { deleteUser } from 'firebase/auth';
import { auth } from '../firebase';

const DeleteAccountPage = () => {
  const { currentUser, logout, approvedCoachUids } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleDelete = async () => {
    if (!currentUser || currentUser.isAnonymous) {
      setError('Turite būti prisijungę (ne anonimiškai) norėdami ištrinti paskyrą.');
      return;
    }
    if (!window.confirm('Ar tikrai norite ištrinti paskyrą?')) {
      return;
    }

    setDeleting(true);
    setError('');
    try {
      const uid = currentUser.uid;
      const profile = await getUserProfile(uid).catch(() => null);
      const coach = getCoachByUid(uid) || (approvedCoachUids?.includes(uid) ? { id: uid, uid } : null);
      if (coach) {
        await removeApprovedCoach(uid);
        await removeCoachFromFirestoreList(coach.id);
        await deleteAllAvailabilitySlotsForCoach(coach.id);
        const email = (profile?.email || currentUser.email || getCoachById(coach.id)?.email || '').trim();
        const name = profile?.fullName || getCoachById(coach.id)?.name || currentUser.displayName || 'Treneris';
        if (email) await sendCoachRemovalEmails(email, name);
      }
      await deleteUserProfileData(uid);
      try {
        await deleteUser(auth.currentUser);
      } catch (authErr) {
        console.warn('Could not delete auth account:', authErr);
        await logout();
      }
      setSuccess(true);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError(err?.message || 'Nepavyko ištrinti paskyros. Pabandykite dar kartą arba susisiekite su mumis.');
    } finally {
      setDeleting(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6, display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Paskyra sėkmingai ištrinta
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Jūsų profilio duomenys buvo pašalinti. Dėkojame, kad naudojotės TenisoUžsiėmimai.lt.
            </Typography>
            <Button component={Link} to="/" variant="contained" sx={{ mt: 3 }}>
              Grįžti į pagrindinį
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 6 }}>
      <Container maxWidth="sm">
        <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
          Ištrinti paskyrą
        </Typography>

        <Paper sx={{ p: 4 }}>
          <Typography variant="body1" paragraph>
            Čia galite visam laikui ištrinti savo TenisoUžsiėmimai.lt paskyrą ir susijusius duomenis.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Bus pašalinta: profilio informacija (vardas, el. paštas, telefonas), nuotrauka ir kita jūsų įvesta informacija.
            Rezervacijų istorija gali išlikti anonimiškai (be asmens duomenų) teisės aktų reikalavimams patenkinti.
          </Typography>

          {!currentUser || currentUser.isAnonymous ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Norėdami ištrinti paskyrą, pirmiausia{' '}
              <Link to="/login">prisijunkite</Link> su el. paštu arba Google.
            </Alert>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                fullWidth
                sx={{ mt: 2, py: 1.5 }}
              >
                {deleting ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 2 }} color="inherit" />
                    Trinama...
                  </>
                ) : (
                  'Ištrinti paskyrą'
                )}
              </Button>
            </>
          )}
        </Paper>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
          <Link to="/privacy">Privatumo politika</Link>
          {' · '}
          <Link to="/">Pagrindinis</Link>
        </Typography>
      </Container>
    </Box>
  );
};

export default DeleteAccountPage;
